import {
    ManagedAgentResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog';

import { useAgentRoster } from '@/hooks/useAgentRoster';

import { composerModeState, selectedModelState } from '@/state/chat';

type SubmitHandler = (
    message: string,
    attachments: any[],
    commandId?: string
) => Promise<string | null>;

interface Options {
    originalAgentId?: string;
    originalAgentName?: string;
    submit: SubmitHandler;
}

interface Guard {
    pinnedAgentName?: string;
    guardedSubmit: SubmitHandler;
    dialog: ReactNode;
}

export function useAgentFallbackGuard({
    originalAgentId,
    originalAgentName,
    submit
}: Options): Guard {
    const { selected, resolveDisplayName } = useAgentRoster();
    const composerMode = useRecoilValue(composerModeState);
    const selectedModel = useRecoilValue(selectedModelState);

    const [accessibleAgents, setAccessibleAgents] = useState<
        ManagedAgentResponseModel[] | null
    >(null);
    useEffect(() => {
        const mgmtApi = new MgmtApi({
            baseUrl: '/mgmt',
            baseApiParams: { credentials: 'include' }
        });
        mgmtApi.api
            .v1ManagedAgentsAccessibleList()
            .then((res) => setAccessibleAgents(res?.data?.data ?? null))
            .catch(() => setAccessibleAgents(null));
    }, []);

    const [open, setOpen] = useState(false);
    const pendingRef = useRef<{
        args: Parameters<SubmitHandler>;
        resolve: (threadId: string | null) => void;
    } | null>(null);

    const originalUnavailable =
        !!originalAgentId &&
        !!accessibleAgents &&
        accessibleAgents.length > 0 &&
        !accessibleAgents.some((agent) => agent.guid === originalAgentId);

    const fallbackAgentName =
        composerMode === 'multiagent'
            ? selected.length > 0
                ? resolveDisplayName(selected)
                : undefined
            : selectedModel?.displayName;
    const fallbackLabel = fallbackAgentName ?? 'your selected agent';

    const guardedSubmit: SubmitHandler = (...args) => {
        if (!originalUnavailable) return submit(...args);
        return new Promise((resolve) => {
            pendingRef.current = { args, resolve };
            setOpen(true);
        });
    };

    const answer = async (proceed: boolean) => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        setOpen(false);
        if (!pending) return;
        pending.resolve(proceed ? await submit(...pending.args) : null);
    };

    const dialog = (
        <AlertDialog open={open}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        Continue with {fallbackLabel}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {originalAgentName ?? 'The agent'} ran this job but is no
                        longer available. {fallbackLabel} can answer, but may
                        not have the same data sources or memory.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => answer(false)}>
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={() => answer(true)}>
                        Continue with {fallbackLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return {
        pinnedAgentName: originalUnavailable ? undefined : originalAgentName,
        guardedSubmit,
        dialog
    };
}
